import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TiStarFullOutline } from 'react-icons/ti';
import { FiShoppingCart } from 'react-icons/fi';
import { FcLike } from 'react-icons/fc';
import {
  Md3dRotation,
  MdOutlineUpdate,
  MdMoreVert,
  MdAccessTime,
  MdOutlineFileDownload,
} from 'react-icons/md';
import { NavLink, useNavigate, useParams } from 'react-router-dom';
import SkinViewer from './SkinViewer';
import ModelingViewer from './ModelingViewer';
import { CheckoutPage } from './CheckoutPage'; // Import the CheckoutPage
import ProductDescription from './ProductDescriptionProps';
import { useCart } from '../../context/CartContext';
import Alert from '../../components/Alert';
import Modal from '../../components/Modal';
import PaymentOption from './paymentOption';
import { instance } from '../../api/axiosInstance';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import ReportModal from './reportModal';
import StarModal from './starModal';
import AddToCartButton from '../../components/AddToCartButton';
import BuyNowButton from '../../components/BuyNowButton';

import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import Slider from 'react-slick';

interface ProductImagesAndDetailsProps {
  product: any;
  selectedImage: string;
  setSelectedImage: (image: string) => void;
  allImages: string[];
  formattedDate: string;
  modelingModelUrl: string;
}

const moreOptions = ['신고하기'];

const ProductImagesAndDetailsMobile: React.FC<ProductImagesAndDetailsProps> = ({
  product,
  selectedImage,
  setSelectedImage,
  allImages,
  formattedDate,
  modelingModelUrl,
}) => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  // Initialize state based on product.productModels
  const [currentModelUrl, setCurrentModelUrl] = useState<string>(
    product.productModels && product.productModels.length > 0
      ? `${process.env.REACT_APP_API_BASE_URL}${product.productModels[0].modelUrl}`
      : modelingModelUrl
  );

  const [currentModelCategory, setCurrentModelCategory] = useState<string>(
    product.productModels && product.productModels.length > 0
      ? product.productModels[0].modelCategory || ''
      : ''
  );

  const [selectedSkin, setSelectedSkin] = useState<string>(''); // 스킨 상태 추가
  const [showAlert, setShowAlert] = useState<boolean>(false);
  const [isMoreOpenDesktop, setIsMoreOpenDesktop] = useState(false);
  const [isMoreOpenMobile, setIsMoreOpenMobile] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false); // 모달 상태 추가
  const [reportModal, setReportModal] = useState<boolean>(false);
  const [clickStar, setClickStar] = useState<boolean>(false);
  const [randomProducts, setRandomProducts] = useState<any[]>([]); // 상태 초기화 시 빈 배열만 사용
  const [isPaymentDone, setIsPaymentDone] = useState<boolean>(false);

  const dropdownRefDesktop = useRef<HTMLDivElement>(null);
  const dropdownRefMobile = useRef<HTMLDivElement>(null);

  // Function to open star modal
  const OpenClickStar = () => {
    setClickStar(true);
  };

  // Function to close star modal
  const closeClickStar = () => {
    setClickStar(false);
  };

  // Function to open report modal
  const OpenreportModal = () => {
    console.log('OpenreportModal called'); // Debugging
    setReportModal(true);
  };

  // Function to close report modal
  const closeReportModal = () => {
    setReportModal(false);
  };

  // Function to handle payment modal
  const handlePaymentModal = () => {
    const accessToken = Cookies.get('access_token');
    if (accessToken) {
      setIsModalOpen(true);
    } else {
      toast.error('로그인을 먼저 진행해주세요.');
      navigate('/login');
    }
  };

  // Function to handle file download
  const handleDownload = async () => {
    console.log('Download button clicked!');
    const accessToken = Cookies.get('access_token');

    if (!accessToken) {
      console.error('Authentication token is missing.');
      toast.error('인증 토큰이 없습니다. 다시 로그인해주세요.');
      return;
    }

    try {
      const response = await instance.get('/auth/files/download', {
        params: { productId: product.id }, // Pass productId as a query parameter
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        responseType: 'blob', // Ensure the response is treated as a blob
      });

      // Extract filename from Content-Disposition header if available
      let filename = `${product.name}.zip`; // Default filename
      const disposition = response.headers['content-disposition'];
      if (disposition && disposition.includes('filename=')) {
        const filenameMatch = disposition.match(/filename="?(.+)"?/);
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1];
        }
      }

      // Create a blob from the response data
      const blob = new Blob([response.data]);

      // Create a link element to trigger the download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename); // Set the filename
      document.body.appendChild(link);
      link.click();

      // Clean up
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download file:', error);
      toast.error('파일 다운로드에 실패했습니다.');
    }
  };

  // Function to close payment modal
  const closeModal = () => {
    setIsModalOpen(false); // 모달 닫기
  };

  // Function to toggle dropdown
  const toggleUserDropdownDesktop = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to document
    setIsMoreOpenDesktop((prev) => !prev); // 드롭다운 상태 토글
  };

  const toggleUserDropdownMobile = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling to document
    setIsMoreOpenMobile((prev) => !prev); // 드롭다운 상태 토글
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRefDesktop.current &&
        !dropdownRefDesktop.current.contains(event.target as Node)
      ) {
        setIsMoreOpenDesktop(false);
      }
      if (
        dropdownRefMobile.current &&
        !dropdownRefMobile.current.contains(event.target as Node)
      ) {
        setIsMoreOpenMobile(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update currentModelUrl and currentModelCategory when product.productModels changes
  useEffect(() => {
    if (
      Array.isArray(product.productModels) &&
      product.productModels.length > 0
    ) {
      setCurrentModelUrl(
        `${process.env.REACT_APP_API_BASE_URL}${product.productModels[0].modelUrl}`
      );
      setCurrentModelCategory(product.productModels[0].modelCategory || '');
    } else {
      setCurrentModelUrl(modelingModelUrl);
      setCurrentModelCategory('');
    }
  }, [product.productModels, modelingModelUrl]);

  // Function to handle thumbnail clicks
  const handleThumbnailClick = (image: string) => {
    if (image === '3D Viewer') {
      setSelectedImage('3D Viewer');
    } else {
      setSelectedImage(image);
    }
  };

  // Function to handle model selection
  const handleModelClick = (modelUrl: string, modelCategory: string) => {
    setCurrentModelUrl(modelUrl);
    setCurrentModelCategory(modelCategory);
  };

  // Function to handle skin selection
  const handleSkinClick = (skinUrl: string) => {
    setSelectedSkin(skinUrl); // 스킨 클릭 시 스킨 URL 업데이트
  };

  // Function to close alert
  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  // Extract skin array for SKINS category
  const skinArray = product.skin
    ? product.skin.map((skin: any) => ({
        skinUrl: skin.skinUrl,
        skinName: skin.skinName,
      }))
    : [];

  const { id } = useParams<{ id: string }>();

  // Function to handle product image click (navigate to product details)
  const handleImageClick = (productId: string) => {
    navigate(`/products/details/${productId}`);
  };

  // Function to fetch random products
  const getRandomProductsFromServer = async (user: string, count: number) => {
    try {
      const response = await instance.get(`/api/products/random`, {
        params: {
          username: user,
          count: count,
        },
      });
      setRandomProducts(response.data); // 응답 데이터를 상태에 저장
    } catch (error) {
      console.error('Failed to fetch random products:', error);
    }
  };

  // Fetch random products on component mount or when product.nickname changes
  useEffect(() => {
    if (product.nickname) {
      getRandomProductsFromServer(product.nickname, 4); // 4개의 랜덤 상품을 가져옴
    }
  }, [product.nickname]);

  // Function to handle profile click (navigate to user profile)
  const handleProfileClick = () => {
    navigate(`/profile/${product.nickname}`); // 해당 유저의 프로필 페이지로 이동
  };

  // Function to check payment status
  useEffect(() => {
    const checkPaymentStatus = async () => {
      const accessToken = Cookies.get('access_token');
      console.log('access_token:', accessToken);

      if (!accessToken) {
        console.error('Authentication token is missing.');
        return;
      }

      try {
        const response = await instance.get('/auth/payments/isDone', {
          params: { productId: product.id },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });
        console.log('Payment status:', response.data);
        setIsPaymentDone(response.data); // 결제 상태 업데이트
      } catch (error) {
        console.error('Failed to check payment status:', error);
      }
    };

    if (product.id) {
      checkPaymentStatus();
    }
  }, [product.id]);

  const sliderSettings = {
    dots: false, // 하단에 슬라이드 점을 표시
    infinite: false, // 무한 루프
    speed: 500, // 슬라이드 속도
    slidesToShow: 2, // 한 번에 보여줄 슬라이드 수
    slidesToScroll: 2, // 한 번에 넘길 슬라이드 수
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
    ],
  };

  return (
    <>
      <div className="overflow-hidden">
        {showAlert && (
          <Alert
            message="상품이 장바구니에 추가되었습니다."
            onClose={handleCloseAlert}
          />
        )}

        {/* Payment Modal */}
        {isModalOpen && (
          <Modal isOpen={isModalOpen} onClose={closeModal}>
            <CheckoutPage
              initialAmount={{ currency: 'KRW', value: product.price }}
              orderName={product.name} // Pass product name as orderName
              productIds={[product.id]}
            />
          </Modal>
        )}

        {/* Consolidated Report Modal */}
        <Modal isOpen={reportModal} onClose={closeReportModal}>
          <ReportModal closeReportModal={closeReportModal} />
        </Modal>

        {/* Star Modal */}
        <Modal isOpen={clickStar} onClose={closeClickStar}>
          <StarModal />
        </Modal>

        <div className="mt-4">
          <div className="flex w-full h-auto flex-col">
            {/* Product Image Section */}
            <div className="w-full h-auto flex flex-col mr-[20px]">
              {/* 3D 상품이미지 */}
              <div className="bg-[#17191d] relative flex w-full h-full justify-center rounded-lg">
                {selectedImage === '3D Viewer' &&
                  product.productModels &&
                  Array.isArray(product.productModels) && (
                    <div className="absolute top-4 left-4 grid grid-cols-2 gap-2 z-10 ">
                      {product.productModels.map(
                        (model: any, index: number) => (
                          <div
                            key={index}
                            className={`px-2 h-8 rounded-lg border-2 flex items-center justify-center text-[10px] cursor-pointer mb-2 ${
                              currentModelUrl ===
                              `${process.env.REACT_APP_API_BASE_URL}${model.modelUrl}`
                                ? 'bg-blue-500 text-white'
                                : 'bg-gray-800 text-white'
                            }`}
                            onClick={() =>
                              handleModelClick(
                                `${process.env.REACT_APP_API_BASE_URL}${model.modelUrl}`,
                                model.modelCategory
                              )
                            }
                          >
                            <span>
                              {model.modelName
                                ? model.modelName
                                : `모델 ${index + 1}`}
                            </span>
                            {(model.modelCategory === 'hat' ||
                              model.modelCategory === 'left-hand' ||
                              model.modelCategory === 'right-hand') && (
                              <button
                                className="ml-2 bg-blue-500 text-white px-2 py-1 rounded"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  console.log(
                                    `${model.modelCategory} 추가 기능 실행`
                                  );
                                }}
                              >
                                {model.modelCategory}
                              </button>
                            )}
                          </div>
                        )
                      )}
                    </div>
                  )}

                {product.category === 'SKINS' ? (
                  selectedImage === '3D Viewer' ? (
                    <div className="w-full h-full">
                      <SkinViewer skins={skinArray} /> {/* 스킨 배열을 전달 */}
                    </div>
                  ) : (
                    <img
                      src={
                        `${process.env.REACT_APP_API_BASE_URL}${selectedImage}` ||
                        `${process.env.REACT_APP_API_BASE_URL}${product.image}`
                      }
                      alt={product.name}
                      className="w-full h-auto object-cover "
                    />
                  )
                ) : product.category === 'MODELING' ||
                  product.category === 'ENTITY' ? (
                  selectedImage === '3D Viewer' ? (
                    <div className="w-full h-full">
                      <ModelingViewer
                        modelUrl={currentModelUrl}
                        modelCategory={currentModelCategory}
                      />
                    </div>
                  ) : (
                    <img
                      src={
                        `${process.env.REACT_APP_API_BASE_URL}${selectedImage}` ||
                        `${process.env.REACT_APP_API_BASE_URL}${product.image}`
                      }
                      alt={product.name}
                      className="w-full h-auto object-cover"
                    />
                  )
                ) : (
                  <img
                    src={
                      `${process.env.REACT_APP_API_BASE_URL}${selectedImage}` ||
                      `${process.env.REACT_APP_API_BASE_URL}${product.image}`
                    }
                    alt={product.name}
                    className="w-full h-auto object-cover"
                  />
                )}
              </div>
              {/* 상품이미지 */}
            </div>

            <div className="">
              {/* Thumbnails */}
              <div className="flex space-x-3 mt-4">
                {allImages.map((image, index) => (
                  <div key={index}>
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL}${image}`}
                      alt={`Thumbnail ${index + 1}`}
                      onClick={() => handleThumbnailClick(image)}
                      className={`w-28 h-28 object-cover cursor-pointer hover:border-2 bg-slate-100 rounded-lg ${
                        selectedImage === image
                          ? 'border-2 border-blue-500'
                          : ''
                      }`}
                    />
                  </div>
                ))}
                {/* 3D Viewer Thumbnail */}
                {(product.category === 'SKINS' ||
                  (product.category === 'MODELING' &&
                    product.productModels &&
                    Array.isArray(product.productModels) &&
                    product.productModels.length > 0) ||
                  (product.category === 'ENTITY' &&
                    product.productModels &&
                    Array.isArray(product.productModels) &&
                    product.productModels.length > 0)) && (
                  <div
                    className="cursor-pointer"
                    onClick={() => handleThumbnailClick('3D Viewer')}
                  >
                    <div className="w-28 h-28 bg-gray-200 rounded-lg border-2 flex items-center justify-center">
                      <Md3dRotation size={40} />
                    </div>
                  </div>
                )}
              </div>
              {/* 상품 디테일 설명 */}
              <div className="flex-col justify-start">
                <div className="box-border w-full flex flex-col mt-7">
                  {/* <div className="box-border flex flex-col px-[50px] pt-[70px] h-[500px] bg-white shadow-lg rounded-lg border relative"> */}
                  <div className="box-border flex flex-col h-[440px] bg-white relative ">
                    {/* 카테고리 * 상품명 */}
                    <p>
                      <div className="inline-block Pretendard-Regular tracking-wider text-[#009dff] border border-[#009dff] px-2 text-xs">
                        {t('product_card.creator_fund')}
                      </div>
                    </p>

                    <span className="text-[11px] Pretendard-Regular tracking-wide text-[#a5a5a5] mt-2">
                      {product.category}
                    </span>

                    <div className="mt-4">
                      <h1 className="text-[26px] text-[#000] Pretendard-Bold tracking-wide">
                        {product.name}
                      </h1>
                    </div>

                    {/* Report Modal is moved outside */}

                    {/* 날짜 * 별점 */}
                    <div className="flex items-center justify-between mt-1 w-full">
                      <div className="flex items-center gap-2">
                        <div className="bg-white shadow-md rounded-full flex items-center gap-1 px-3 py-1 border border-black/10 text-[10px] text-[#828282]">
                          <TiStarFullOutline className="mb-[2px]" />
                          <span className="font-semibold text-[#545454]">
                            {product.review}
                          </span>
                        </div>
                        <div className="bg-white shadow-md rounded-full flex items-center gap-[6px] px-3 py-1 border border-black/10 text-[10px] text-[#828282]">
                          <MdAccessTime className="mb-[2px]" />
                          <span className="font-medium text-[#545454]">
                            {formattedDate}
                          </span>
                        </div>
                      </div>
                      <div>
                        {/* Desktop Dropdown */}
                        <div className="bg-white shadow-md cursor-pointer rounded-full flex items-center gap-[6px] px-[6px] py-[6px] border border-black/10 text-[13px] text-[#828282]">
                          <div ref={dropdownRefDesktop}>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={12}
                              height={12}
                              viewBox="0 0 24 24"
                              onClick={toggleUserDropdownDesktop}
                              className="cursor-pointer"
                            >
                              <path
                                fill="#545454"
                                d="M5 10c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2m14 0c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2m-7 0c-1.1 0-2 .9-2 2s.9 2 2 2s2-.9 2-2s-.9-2-2-2"
                              ></path>
                            </svg>
                            {isMoreOpenDesktop && (
                              <div className="absolute right-3 mt-4 bg-white border rounded-lg shadow-lg max-w-[80px] w-full">
                                <ul>
                                  {moreOptions.map((option, index) => (
                                    <li
                                      key={index}
                                      className="px-4 py-2 hover:bg-gray-100 text-xs text-left cursor-pointer"
                                      onClick={OpenreportModal}
                                    >
                                      {option}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 금액 */}
                    <div className="mt-6 mb-3">
                      <h3 className="Pretendard-Regular text-[#000] text-sm">
                        상품금액
                      </h3>
                      <div className="mt-2">
                        <span className="line-through Pretendard-Regular font-light text-xs text-[#acacac]">
                          <p>
                            ({t('product_card.moneyterms')}{' '}
                            {product.originalPrice.toLocaleString()})
                          </p>
                        </span>
                        <div className="flex items-center gap-2 mt-[2px] leading-11">
                          <span className="Pretendard-Bold text-[#009dff] text-base">
                            {product.discount}%
                          </span>
                          <span className="Pretendard-Bold text-[#000] text-base">
                            {t('product_card.moneyterms')}{' '}
                            {product.price.toLocaleString()}
                          </span>
                        </div>
                      </div>
                    </div>
                    <hr className="w-full mt-4 border rounded-full border-dashed border-[#eeeeee]" />

                    {/* 유저명 */}
                    <div className="mt-6">
                      <h3 className="Pretendard-Regular text-[#000000] text-sm">
                        Owner
                      </h3>
                      <div
                        className="flex items-center gap-4 mt-2"
                        onClick={handleProfileClick}
                      >
                        <div className="w-[40px] h-[40px] relative">
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL}${product.profileImageUrl}`}
                            alt={`${product.nickname}'s profile`}
                            className="w-full h-full object-cover object-center rounded-full cursor-pointer"
                          />
                          <div className="absolute bottom-[-2px] right-[-5px]">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width={18}
                              height={18}
                              viewBox="0 0 48 48"
                            >
                              <g
                                fill="none"
                                strokeLinejoin="round"
                                strokeWidth={3}
                              >
                                <path
                                  fill="#009dff"
                                  stroke="#009dff"
                                  d="M24 44C29.5228 44 34.5228 41.7614 38.1421 38.1421C41.7614 34.5228 44 29.5228 44 24C44 18.4772 41.7614 13.4772 38.1421 9.85786C34.5228 6.23858 29.5228 4 24 4C18.4772 4 13.4772 6.23858 9.85786 9.85786C6.23858 13.4772 4 18.4772 4 24C4 29.5228 6.23858 34.5228 9.85786 38.1421C13.4772 41.7614 18.4772 44 24 44Z"
                                ></path>
                                <path
                                  stroke="#fff"
                                  strokeLinecap="round"
                                  d="M16 24L22 30L34 18"
                                ></path>
                              </g>
                            </svg>
                          </div>
                        </div>
                        <span className=" Pretendard-Bold cursor-pointer text-sm leading-9 text-[#000000] hover:underline">
                          {product.nickname}
                        </span>
                      </div>
                    </div>

                    {/* 구매하기 * 장바구니 * 찜 */}
                    <div className="absolute bottom-0 w-[100%] box-border">
                      <div className="flex items-center gap-2">
                        <BuyNowButton
                          isPaymentDone={isPaymentDone}
                          handlePaymentModal={handlePaymentModal}
                          handleDownload={handleDownload}
                          product={product}
                        />
                        <AddToCartButton
                          product={product}
                          selectedImage={selectedImage}
                          setShowAlert={setShowAlert}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* 상품소개 */}
              {/* Product Description */}
              <ProductDescription
                description={product.description || ''}
                usage={[product.usageInstructions]}
                tags={[]}
                tokenId="245206790664626990743925879781143737219667204759271722669999999999999999"
              />
            </div>
          </div>
        </div>
        {/* 다른 상품 */}
        <div className="w-full pb-[30px]">
          <div>
            {/* Random Products (Desktop) */}
            <div>
              {randomProducts.length > 0 && (
                <>
                  <div className="mt-12">
                    <h3 className="text-sm text-[#46433f] Pretendard-Medium">
                      {t('profile.creatorProducts')} {/* 다국어 지원 추가 */}
                    </h3>
                  </div>
                  <div className="w-full box-bordermt-2 mt-4">
                    <Slider {...sliderSettings}>
                      {randomProducts.map((p) => (
                        <div key={p.id} className="px-[3px]">
                          {' '}
                          {/* 양쪽에 3px씩 padding을 주어 간격이 6px이 되도록 설정 */}
                          <img
                            src={`${process.env.REACT_APP_API_BASE_URL}${p.image}`}
                            alt={`Product ${p.name}`}
                            className="w-full h-auto object-cover transition-transform duration-300 ease-in-out hover:scale-110 cursor-pointer rounded-lg"
                            onClick={() => handleImageClick(p.id.toString())}
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProductImagesAndDetailsMobile;
